import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { createToolbarEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import { leaveConference } from '../../../base/conference/actions';
import ContextMenu from '../../../base/ui/components/web/ContextMenu';
import { queueService } from '../../../interpreter-queue/InterpreterQueueService';
import { ENDING_FOR_ALL_STATUS, HANGUP_MENU_LABEL, endCallForEveryone, endForAllFailureMessage, isGroupCallHost, pageRoomName } from '../../../interpreter-queue/groupEndForAll';
import { isVriRoomName, isVriSessionStarter } from '../../../interpreter-queue/vriSessionHangup';
import { showNotification } from '../../../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../../../notifications/constants';
import { getPersistentItem, getPersistentJson } from '../../../vrs-auth/storage';
import HangupButton from '../HangupButton';
import { VriEndCallButton, VriLeaveCallButton } from '../VriHangupButton';
import GroupHangupChoices from './GroupHangupChoices';
import HangupMenuButton from './HangupMenuButton';
/**
 * Whether the signed-in person hosts the group call stored for this room.
 * Read from the stored call (the server's calls.client_id and its group
 * flag), never from Jitsi moderator status: every member of a platform room
 * holds a moderator token. The stored call is shared by every tab, so it
 * counts only when it names the room this page is in.
 *
 * @returns {boolean}
 */
export function readGroupCallHost() {
    const user = getPersistentJson('vrs_user_info');
    const call = getPersistentJson('vrs_active_call');
    return isGroupCallHost(call, user?.id, getPersistentItem('vrs_user_role'), pageRoomName());
}
/**
 * In a VRI session's room (`vri-`), whether this person started the session:
 * their hang-up ends it for everyone ("End call"); everyone else only
 * leaves ("Leave call"). Robert, 2026-10-09 (vriSessionHangup.ts).
 *
 * @param {string} [roomTokenRole] - The room token's role: a guest's never counts.
 * @returns {boolean}
 */
export function readVriSessionStarter(roomTokenRole) {
    const user = getPersistentJson('vrs_user_info');
    return isVriSessionStarter(getPersistentJson('vrs_active_call'), user?.id, getPersistentItem('vrs_user_role'), pageRoomName(), roomTokenRole);
}
const useStyles = makeStyles()(() => {
    return {
        hangupMenu: {
            position: 'relative',
            right: 'auto',
            display: 'flex',
            flexDirection: 'column',
            rowGap: '8px',
            margin: 0,
            padding: '16px',
            marginBottom: '4px',
            minWidth: '240px'
        }
    };
});
/**
 * The room's hang-up. In a group call its host gets a small menu, opened by
 * "Leave or end call": "Leave call" (a plain hang-up: the call goes on while
 * 2 or more people are left in it) or "End call for everyone"
 * (p2p_end_for_all). Everyone else, and every direct call, keeps the single
 * hang-up button. What End call for everyone came to is also said in a
 * polite live region, since the room's toasts are not announced.
 *
 * @param {IProps} props - Component props.
 * @returns {JSX.Element}
 */
export default function GroupCallHangupButton(props) {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const [host, setHost] = useState(readGroupCallHost);
    const roomTokenRole = useSelector((state) => state['features/base/jwt']?.user?.role);
    // What End call for everyone came to, for screen readers and braille:
    // the room's toasts are not a live region.
    const [status, setStatus] = useState('');
    const ending = useRef(false);
    const propsRef = useRef(props);
    propsRef.current = props;
    useEffect(() => {
        const refresh = () => {
            setHost(readGroupCallHost());
        };
        refresh();
        // Only a client can host a platform call. The call may be stored only
        // after this mounts (a reloaded group room is seated again first), so
        // listen in any client room; the queue middleware records these on
        // the stored call before this hears them.
        if (getPersistentItem('vrs_user_role') !== 'client') {
            return;
        }
        queueService.on('p2pParticipantJoined', refresh);
        // The host leaving passes the host role on (hostId).
        queueService.on('p2pParticipantLeft', refresh);
        queueService.on('p2pAccepted', refresh);
        queueService.on('p2pJoinRoom', refresh);
        return () => {
            queueService.off('p2pParticipantJoined', refresh);
            queueService.off('p2pParticipantLeft', refresh);
            queueService.off('p2pAccepted', refresh);
            queueService.off('p2pJoinRoom', refresh);
        };
    }, []);
    const leave = useCallback(() => {
        propsRef.current.onVisibilityChange(false);
        sendAnalytics(createToolbarEvent('hangup'));
        dispatch(leaveConference());
    }, [dispatch]);
    const endForAll = useCallback(() => {
        propsRef.current.onVisibilityChange(false);
        if (ending.current) {
            return;
        }
        const notify = (message) => {
            setStatus(message);
            dispatch(showNotification({
                titleKey: undefined,
                title: message,
                descriptionKey: undefined,
                uid: 'p2p-end-for-all-failed'
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        };
        // Checked again on the press: another tab may have stored another call.
        if (!readGroupCallHost()) {
            setHost(false);
            notify(endForAllFailureMessage('NOT_THIS_ROOM'));
            return;
        }
        ending.current = true;
        setStatus(ENDING_FOR_ALL_STATUS);
        sendAnalytics(createToolbarEvent('end.meeting'));
        const call = getPersistentJson('vrs_active_call');
        endCallForEveryone({
            call: typeof call?.callId === 'string' && call.callId ? {
                callId: call.callId,
                roomName: typeof call.roomName === 'string' ? call.roomName : undefined
            } : null,
            leave: () => dispatch(leaveConference()),
            notify,
            service: queueService
        }).then(outcome => {
            if (!outcome.ok) {
                ending.current = false;
                setHost(readGroupCallHost());
            }
        });
    }, [dispatch]);
    const liveStatus = (React.createElement("span", { "aria-live": 'polite', className: 'sr-only', key: 'hangup-status', role: 'status' }, status));
    if (!host && isVriRoomName(pageRoomName())) {
        const VriHangupButton = readVriSessionStarter(roomTokenRole) ? VriEndCallButton : VriLeaveCallButton;
        return (React.createElement(React.Fragment, null,
            React.createElement(VriHangupButton, { buttonKey: 'hangup', customClass: 'hangup-button', key: 'hangup-button', notifyMode: props.notifyMode, visible: true }),
            liveStatus));
    }
    if (!host) {
        return (React.createElement(React.Fragment, null,
            React.createElement(HangupButton, { buttonKey: 'hangup', customClass: 'hangup-button', key: 'hangup-button', notifyMode: props.notifyMode, visible: true }),
            liveStatus));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(HangupMenuButton, { ariaControls: 'hangup-menu', isOpen: props.isOpen, key: 'hangup-menu', menuLabel: HANGUP_MENU_LABEL, notifyMode: props.menuNotifyMode, onVisibilityChange: props.onVisibilityChange },
            React.createElement(ContextMenu, { accessibilityLabel: HANGUP_MENU_LABEL, className: classes.hangupMenu, hidden: false, id: 'hangup-menu', inDrawer: props.inDrawer, onKeyDown: props.onEscKey },
                React.createElement(GroupHangupChoices, { endNotifyMode: props.endNotifyMode, leaveNotifyMode: props.notifyMode, onEndForAll: endForAll, onLeave: leave }))),
        liveStatus));
}
